import { useCallback, useEffect, useRef } from 'react';
import { startTour as sendStartTourMessage } from '../communication/startTour';
import { stopTour as sendStopTourMessage } from '../communication/stopTour';
import { LOGGER_NAME, setupLogger } from '../logging/setupLogger';
import { Logger } from 'simple-logging-lib';
import { Party } from '../communication/sync.messages';
import { partyAlive } from '../communication/partyAlive';
import { useEffectOnce } from './useEffectOnce';
import { useMeasurePerformance } from './useMeasurePerformance';

/**
 * This hook is used to start the onboarding tour.
 * This hook will automatically start the tour.
 * To control the tour, one can use the `stopTour` and `startTour` functions.
 *
 * @returns `tourRunning` - whether the tour is running (this does not mean it is active as another tour may be active and this tour might be waiting in a queue)
 * @returns `startTour` - start the tour (idempotent), this will add it to a queue if another tour is already active
 * @returns `stopTour` - stop the tour (idempotent), this will remove the tour if active and remove it from the queue if it is waiting
 */
export const useStartOnboardingTour = tour => {
  const {
    getDuration
  } = useMeasurePerformance();
  const tourRunning = useRef(false);
  const stopTourRef = useRef(false);
  useEffectOnce(() => {
    setupLogger();
  });
  const startTour = useCallback(() => {
    stopTourRef.current = false;
    const logger = Logger.getLogger(LOGGER_NAME);
    return new Promise((resolve, reject) => {
      sendStartTourMessage(tour).then(() => {
        tourRunning.current = true;
        const {
          startTime,
          endTime,
          durationMs
        } = getDuration();
        logger.debug(`Onboarding tour started successfully: ${tour.tourId} (started at ${startTime}, took ${durationMs}ms, ended at ${endTime})`);
        resolve();
      }).catch(error => {
        logger.error(`Failed to start onboarding tour: ${error}`);
        reject(error);
      });
    });
  }, [tour, getDuration]);
  const stopTour = useCallback(() => {
    const logger = Logger.getLogger(LOGGER_NAME);
    stopTourRef.current = true;
    return new Promise((resolve, reject) => {
      sendStopTourMessage(tour.tourId).then(() => {
        tourRunning.current = false;
        logger.debug(`Onboarding tour stopped successfully: ${tour.tourId}`);
        resolve();
      }).catch(error => {
        logger.error(`Failed to stop onboarding tour: ${error}`);
        reject(error);
      });
    });
  }, [tour]);
  useEffect(() => {
    const unsubscribe = partyAlive(Party.APP_STARTING_TOUR, party => {
      if (party === Party.NAV_ONBOARDING_TOURS_CONSUMER) {
        if (stopTourRef.current) {
          // The tour was stopped before it could start.
          return;
        }
        void startTour();
      }
    });
    return () => {
      unsubscribe();
    };
  }, [tour, getDuration, startTour]);
  return {
    tourRunning: tourRunning.current,
    startTour,
    stopTour
  };
};